import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Check, GraduationCap } from "lucide-react";

/**
 * Reusable Classes CRUD dialog.
 * Props:
 *  - open, onOpenChange
 *  - onChanged()  optional callback after any mutation (so parent can refresh)
 */
export default function ClassesManager({ open, onOpenChange, onChanged }) {
  const [classes, setClasses] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const loadRef = useRef(null);
  useEffect(() => {
    loadRef.current = async () => {
      try {
        const { data } = await teacherApi.get("/classes");
        setClasses(data);
      } catch {
        toast.error("Błąd ładowania klas");
      }
    };
  });
  const load = () => loadRef.current?.();

  useEffect(() => { if (open) load(); }, [open]);

  const reset = () => {
    setEditingId(null); setName(""); setDescription("");
  };

  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Podaj nazwę klasy"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await teacherApi.put(`/classes/${editingId}`, { name: n, description });
        toast.success("Klasa zapisana");
      } else {
        await teacherApi.post("/classes", { name: n, description });
        toast.success("Klasa utworzona");
      }
      reset();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zapisać");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id); setName(c.name); setDescription(c.description || "");
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await teacherApi.delete(`/classes/${toDelete.id}`);
      toast.success("Klasa usunięta");
      if (editingId === toDelete.id) reset();
      setToDelete(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="brut bg-white rounded-md border-2 border-[#0F172A] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl inline-flex items-center gap-2">
              <GraduationCap className="w-6 h-6" strokeWidth={2.5} />Klasy
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Twórz klasy i przypisuj do nich nowe kartkówki, aby filtrować dashboard po klasie.
            </DialogDescription>
          </DialogHeader>

          <div className="brut bg-[#F8FAFC] rounded-md p-4">
            <Label className="font-bold text-sm">
              {editingId ? "Edytuj klasę" : "Nowa klasa"}
            </Label>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="np. 1A"
                maxLength={40}
                className="brut-input border-2 border-[#0F172A] rounded-md h-10"
                data-testid="class-name-input"
              />
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Opis (opcjonalnie)"
                maxLength={120}
                className="brut-input border-2 border-[#0F172A] rounded-md h-10"
                data-testid="class-description-input"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={save}
                disabled={busy}
                className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
                data-testid="save-class-btn"
              >
                {editingId ? (<><Check className="w-4 h-4 mr-1" strokeWidth={2.5} />Zapisz</>) : (<><Plus className="w-4 h-4 mr-1" strokeWidth={2.5} />Dodaj</>)}
              </Button>
              {editingId && (
                <Button
                  variant="outline"
                  onClick={reset}
                  className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                  data-testid="cancel-edit-class-btn"
                >
                  <X className="w-4 h-4 mr-1" strokeWidth={2.5} />Anuluj
                </Button>
              )}
            </div>
          </div>

          <div className="mt-2">
            <Label className="font-bold text-sm">Twoje klasy ({classes.length})</Label>
            {classes.length === 0 ? (
              <div className="brut bg-white rounded-md p-6 mt-2 text-center text-sm text-[#64748B]" data-testid="no-classes-msg">
                Brak klas. Dodaj pierwszą klasę powyżej.
              </div>
            ) : (
              <ul className="mt-2 space-y-2 max-h-72 overflow-y-auto">
                {classes.map((c) => (
                  <li
                    key={c.id}
                    className="brut bg-white rounded-md p-3 flex items-center justify-between gap-3"
                    data-testid={`class-row-${c.name}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display font-bold">{c.name}</div>
                      {c.description && (
                        <div className="text-xs text-[#64748B] truncate">{c.description}</div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEdit(c)}
                        className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-8"
                        data-testid={`edit-class-${c.name}`}
                      >
                        <Pencil className="w-3 h-3" strokeWidth={2.5} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setToDelete(c)}
                        className="brut bg-white border-2 border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] hover:text-[#EF4444] rounded-md h-8"
                        data-testid={`delete-class-${c.name}`}
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="delete-class-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Usunąć klasę?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && (
                <>Klasa <b>{toDelete.name}</b> zostanie usunięta. Istniejące kartkówki tej klasy pozostaną w bazie, ale stracą przypisanie.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="delete-class-cancel-btn"
            >
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md"
              data-testid="delete-class-confirm-btn"
            >
              Tak, usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
